import {
  Eye,
  FileText,
  LoaderCircle,
  PackageCheck,
  Plus,
  RefreshCw,
  Search,
  ShoppingBasket,
  Trash2,
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom'

import {
  formatearKilosDiferencia,
  RESULTADOS_DIFERENCIA,
} from '../../components/abastecimiento/diferenciaCompraPrevia'
import EliminarCompraModal from '../../components/compras/EliminarCompraModal'
import FacturaModal from '../../components/abastecimiento/FacturaModal'
import RecepcionModal from '../../components/abastecimiento/RecepcionModal'
import {
  CampoFiltro,
  estiloInputBusqueda,
  estiloInputFiltro,
} from '../../components/pos/FiltrosPosComunes'
import ModalMensaje from '../../components/ui/ModalMensaje'
import Paginacion from '../../components/ui/Paginacion'
import { useEmpresa } from '../../context/EmpresaContext'
import { useModalMensaje } from '../../hooks/useModalMensaje'
import { usePaginacion } from '../../hooks/usePaginacion'
import { supabase } from '../../lib/supabase'
import {
  cargarComprasHistoricas,
  cargarFacturasPorCompra,
  cargarRecepcionesPorCompra,
} from '../../services/abastecimiento'
import {
  compraAdmiteRecepcion,
  evaluarSeleccionFactura,
  obtenerEstadoRecepcion,
  obtenerModalidad,
  obtenerMontoCompra,
  puedeRegistrarFactura,
  puedeRegistrarRecepcion,
  puedeSeleccionarParaFactura,
  puedeValidarRecepcion,
} from '../../utils/abastecimiento'
import {
  formatearKilosCompra,
  formatearMontoCompra,
  nombreProveedorCompra,
  obtenerEstadoCompra,
  puedeEliminarCompra,
} from '../../utils/compras'
import { formatearFechaPos } from '../../utils/pos'

// Tope de filas por consulta (límite por defecto de PostgREST). Si se
// alcanza, se avisa para acotar con filtros en vez de truncar en silencio.
const LIMITE_COMPRAS = 1000

function normalizarTexto(valor) {
  return String(valor || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
}

function MiniDato({
  etiqueta,
  valor,
  destacado = false,
}) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {etiqueta}
      </p>

      <p
        className={`mt-0.5 truncate tabular-nums ${
          destacado
            ? 'font-bold text-emerald-700'
            : 'font-semibold text-slate-900'
        }`}
      >
        {valor}
      </p>
    </div>
  )
}

function Compras() {
  const navigate = useNavigate()
  const location = useLocation()
  const { empresaActual } = useEmpresa()

  const { modal, abrirModal, cerrarModal } = useModalMensaje()

  const empresaId = empresaActual?.id
  const rol = empresaActual?.rol
  // Consulta y POS Venta no registran ni modifican nada de Abastecimiento.
  const esConsulta = !puedeRegistrarRecepcion(rol)
  const puedeFacturar = puedeRegistrarFactura(rol)
  const puedeEliminar = puedeEliminarCompra(rol)

  const [compras, setCompras] = useState([])
  const [recepciones, setRecepciones] = useState(new Map())
  const [facturasPorCompra, setFacturasPorCompra] = useState(new Map())
  const [seleccion, setSeleccion] = useState([])
  const [compraRecepcion, setCompraRecepcion] = useState(null)
  const [facturaAbierta, setFacturaAbierta] = useState(false)
  const [compraEliminar, setCompraEliminar] = useState(null)
  const [productos, setProductos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')

  const [busqueda, setBusqueda] = useState('')
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('')
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('')
  const [filtroProducto, setFiltroProducto] = useState('')
  const [filtroEstado, setFiltroEstado] = useState('')

  useEffect(() => {
    if (!empresaId) return

    let cancelado = false

    const cargarProductos = async () => {
      const { data, error: errorProductos } = await supabase
        .from('productos')
        .select('id, nombre')
        .eq('empresa_id', empresaId)
        .order('nombre', { ascending: true })

      if (cancelado) return

      if (errorProductos) {
        console.error('Error al cargar productos:', errorProductos)
        return
      }

      setProductos(data || [])
    }

    setFiltroProducto('')
    cargarProductos()

    return () => {
      cancelado = true
    }
  }, [empresaId])

  const cargarCompras = useCallback(async () => {
    if (!empresaId) return

    setCargando(true)
    setError('')

    try {
      let consulta = supabase
        .from('vw_abastecimientos_resumen')
        .select(
          'id, codigo_compra, numero_compra, fecha_compra, proveedor_id, proveedor_razon_social, proveedor_nombre_fantasia, producto_nombre, documento, modalidad, cantidad_acordada_kg, peso_neto_kg, kilos_brutos_recepcion_kg, diferencia_kilos_kg, resultado_diferencia_kilos, precio_kilo, monto_estimado, monto_logistico_registrado, estado'
        )
        .eq('empresa_id', empresaId)

      if (filtroFechaDesde) {
        consulta = consulta.gte('fecha_compra', filtroFechaDesde)
      }

      if (filtroFechaHasta) {
        consulta = consulta.lte('fecha_compra', filtroFechaHasta)
      }

      if (filtroProducto) {
        consulta = consulta.eq('producto_id', filtroProducto)
      }

      if (filtroEstado) {
        consulta = consulta.eq('estado', filtroEstado)
      }

      const { data, error: errorConsulta } = await consulta
        .order('fecha_compra', { ascending: false })
        .order('numero_compra', { ascending: false })
        .range(0, LIMITE_COMPRAS - 1)

      if (errorConsulta) throw errorConsulta

      const lista = data || []
      const ids = lista.map((compra) => compra.id)

      const [listaRecepciones, historicas, vinculos] = await Promise.all([
        cargarRecepcionesPorCompra(empresaId, ids),
        cargarComprasHistoricas(empresaId),
        cargarFacturasPorCompra(empresaId, ids),
      ])

      const historicasSet = new Set(historicas)

      setRecepciones(
        new Map(listaRecepciones.map((recepcion) => [recepcion.compra_id, recepcion]))
      )
      setFacturasPorCompra(
        new Map(vinculos.map((vinculo) => [vinculo.compra_id, vinculo.factura_id]))
      )
      setSeleccion([])
      setCompras(
        lista.map((compra) => ({
          ...compra,
          recepcion_automatica_habilitada: !historicasSet.has(compra.id),
        }))
      )
    } catch (errorCarga) {
      console.error('Error al cargar compras:', errorCarga)
      setError(
        errorCarga.message || 'No fue posible cargar las compras.'
      )
    } finally {
      setCargando(false)
    }
  }, [
    empresaId,
    filtroFechaDesde,
    filtroFechaHasta,
    filtroProducto,
    filtroEstado,
  ])

  useEffect(() => {
    cargarCompras()
  }, [cargarCompras])

  // Aviso de éxito cuando la compra se eliminó desde el detalle.
  useEffect(() => {
    const codigoEliminada = location.state?.compraEliminada

    if (!codigoEliminada) return

    abrirModal({
      tipo: 'success',
      titulo: 'Compra eliminada',
      mensaje: `La compra ${codigoEliminada} fue eliminada definitivamente.`,
    })
    navigate(location.pathname, { replace: true, state: null })
  }, [location, navigate, abrirModal])

  const comprasFiltradas = useMemo(() => {
    const texto = normalizarTexto(busqueda)

    if (!texto) return compras

    return compras.filter((compra) =>
      normalizarTexto(
        `${compra.proveedor_razon_social || ''} ${
          compra.proveedor_nombre_fantasia || ''
        }`
      ).includes(texto)
    )
  }, [compras, busqueda])

  const filtrosKey = [
    empresaId,
    busqueda,
    filtroFechaDesde,
    filtroFechaHasta,
    filtroProducto,
    filtroEstado,
  ].join('|')

  const {
    pagina,
    setPagina,
    tamanioPagina,
    setTamanioPagina,
    totalPaginas,
    indiceInicio,
    indiceFin,
  } = usePaginacion({
    totalRegistros: comprasFiltradas.length,
    filtrosKey,
  })

  const comprasPaginadas = comprasFiltradas.slice(
    (pagina - 1) * tamanioPagina,
    pagina * tamanioPagina
  )

  const hayFiltrosActivos = Boolean(
    busqueda ||
      filtroFechaDesde ||
      filtroFechaHasta ||
      filtroProducto ||
      filtroEstado
  )

  const limpiarFiltros = () => {
    setBusqueda('')
    setFiltroFechaDesde('')
    setFiltroFechaHasta('')
    setFiltroProducto('')
    setFiltroEstado('')
  }

  const verCompra = (compra) => {
    navigate(`/compras/${compra.id}`)
  }

  const alternarSeleccion = (compraId) => {
    setSeleccion((actual) =>
      actual.includes(compraId)
        ? actual.filter((id) => id !== compraId)
        : [...actual, compraId]
    )
  }

  const comprasSeleccionadas = compras.filter((compra) =>
    seleccion.includes(compra.id)
  )

  const abrirFactura = () => {
    const evaluacion = evaluarSeleccionFactura(
      comprasSeleccionadas,
      recepciones
    )

    if (evaluacion.error) {
      abrirModal({
        tipo: evaluacion.revisionAdministrativa ? 'warning' : 'error',
        titulo: evaluacion.revisionAdministrativa
          ? 'Requiere revisión administrativa'
          : 'Revisa la selección',
        mensaje: evaluacion.error,
      })
      return
    }

    setFacturaAbierta(true)
  }

  const alRegistrarFactura = (facturaId) => {
    setFacturaAbierta(false)

    abrirModal({
      tipo: 'success',
      titulo: 'Factura registrada',
      mensaje:
        'Se registró la factura y se generó un único gasto pendiente.',
      textoAceptar: 'Ver gasto',
      onAceptar: async () => {
        cerrarModal()

        const { data } = await supabase
          .from('vw_facturas_proveedor_resumen')
          .select('gasto_id')
          .eq('empresa_id', empresaId)
          .eq('id', facturaId)
          .maybeSingle()

        if (data?.gasto_id) {
          navigate(`/gastos?ver=${data.gasto_id}`)
        } else {
          navigate('/gastos')
        }
      },
      mostrarCancelar: true,
      textoCancelar: 'Cerrar',
      onCancelar: cerrarModal,
    })

    cargarCompras()
  }

  const verGastoExistente = (gastoId) => {
    setFacturaAbierta(false)
    navigate(gastoId ? `/gastos?ver=${gastoId}` : '/gastos')
  }

  const alGuardarRecepcion = (mensaje) => {
    setCompraRecepcion(null)
    abrirModal({
      tipo: 'success',
      titulo: 'Recepción',
      mensaje,
    })
    cargarCompras()
  }

  const alEliminarCompra = (resultado, preview) => {
    const eliminadaId = compraEliminar?.id
    const codigo = preview?.codigo_compra || compraEliminar?.codigo_compra

    setCompraEliminar(null)

    // Sin datos residuales de la compra eliminada.
    if (eliminadaId) {
      setCompras((actual) => actual.filter((c) => c.id !== eliminadaId))
      setSeleccion((actual) => actual.filter((id) => id !== eliminadaId))
      setRecepciones((actual) => {
        const copia = new Map(actual)
        copia.delete(eliminadaId)
        return copia
      })
      setFacturasPorCompra((actual) => {
        const copia = new Map(actual)
        copia.delete(eliminadaId)
        return copia
      })
    }

    abrirModal({
      tipo: 'success',
      titulo: 'Compra eliminada',
      mensaje: `La compra ${codigo} fue eliminada definitivamente.`,
    })
    cargarCompras()
  }

  // Bloque reutilizado en tarjetas móviles y tabla: estado + acciones.
  const renderBadges = (compra) => {
    const modalidad = obtenerModalidad(compra.modalidad)
    const estado = obtenerEstadoCompra(compra.estado)
    const estadoRecepcion = obtenerEstadoRecepcion(
      recepciones.get(compra.id)
    )

    // Indicador discreto: solo compras PREVIA con recepción definitiva y
    // diferencia distinta de cero.
    // La diferencia (peso neto recibido − acordado) la calcula Supabase.
    const estadoDiferencia = recepciones.get(compra.id)?.estado
    const resultadoDiferencia = String(
      compra.resultado_diferencia_kilos || ''
    ).toLowerCase()
    const mostrarDiferencia =
      compra.modalidad === 'PREVIA' &&
      compra.estado !== 'anulada' &&
      ['VALIDADA', 'RECHAZADA_TOTAL'].includes(estadoDiferencia) &&
      ['faltante', 'excedente'].includes(resultadoDiferencia) &&
      Number.isFinite(Number(compra.diferencia_kilos_kg))

    return (
      <div className="flex flex-wrap gap-1.5">
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${modalidad.clase}`}
        >
          {modalidad.etiqueta}
        </span>

        <span
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${estado.clase}`}
        >
          {estado.etiqueta}
        </span>

        {compra.estado !== 'anulada' &&
          compra.recepcion_automatica_habilitada !== false && (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${estadoRecepcion.clase}`}
            >
              {estadoRecepcion.etiqueta}
            </span>
          )}

        {mostrarDiferencia && (
          <span
            title="Diferencia entre kilos netos recibidos y kilos acordados"
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
              RESULTADOS_DIFERENCIA[resultadoDiferencia].clase
            }`}
          >
            {formatearKilosDiferencia(Number(compra.diferencia_kilos_kg), {
              conSigno: true,
            })}
          </span>
        )}

        {facturasPorCompra.has(compra.id) && (
          <span className="inline-flex items-center rounded-full bg-violet-100 px-2.5 py-1 text-xs font-semibold text-violet-700">
            Facturada
          </span>
        )}
      </div>
    )
  }

  const renderAcciones = (compra, mobile) => {
    const recepcion = recepciones.get(compra.id)
    const puedeRecibir =
      !esConsulta && compraAdmiteRecepcion(compra, recepcion)

    const claseBoton = mobile
      ? 'flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold transition'
      : 'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition'

    return (
      <>
        {puedeRecibir && (
          <button
            type="button"
            onClick={() => setCompraRecepcion(compra)}
            className={`${claseBoton} border-emerald-600 text-emerald-700 hover:bg-emerald-50`}
          >
            <PackageCheck size={16} />
            {recepcion ? 'Recepción' : 'Recibir'}
          </button>
        )}

        <button
          type="button"
          onClick={() => verCompra(compra)}
          className={`${claseBoton} border-slate-300 text-slate-600 hover:bg-slate-100`}
        >
          <Eye size={16} />
          Ver
        </button>

        {puedeEliminar && (
          <button
            type="button"
            onClick={() => setCompraEliminar(compra)}
            aria-label={`Eliminar compra ${compra.codigo_compra}`}
            title="Eliminar compra"
            className={`${claseBoton} border-red-200 text-red-600 hover:bg-red-50`}
          >
            <Trash2 size={16} />
            Eliminar
          </button>
        )}
      </>
    )
  }

  const renderCasilla = (compra) => {
    if (!puedeFacturar) return null

    const seleccionable = puedeSeleccionarParaFactura(
      compra,
      recepciones.get(compra.id),
      facturasPorCompra.has(compra.id)
    )

    return (
      <input
        type="checkbox"
        checked={seleccion.includes(compra.id)}
        disabled={!seleccionable}
        onChange={() => alternarSeleccion(compra.id)}
        aria-label={`Seleccionar ${compra.codigo_compra} para factura`}
        className="h-5 w-5 accent-emerald-600 disabled:opacity-30"
      />
    )
  }

  const renderContenido = () => {
    if (cargando && compras.length === 0) {
      return (
        <div className="flex min-h-64 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="text-center">
            <LoaderCircle
              size={32}
              className="mx-auto animate-spin text-emerald-600"
            />

            <p className="mt-3 font-medium text-slate-500">
              Cargando compras...
            </p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <h3 className="text-lg font-bold text-red-600">
            No fue posible cargar las compras
          </h3>

          <p className="mt-2 text-slate-600">{error}</p>
        </div>
      )
    }

    if (comprasFiltradas.length === 0) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <ShoppingBasket
            size={36}
            className="mx-auto text-slate-300"
          />

          <h3 className="mt-3 text-lg font-bold text-slate-900">
            {hayFiltrosActivos
              ? 'No hay compras con estos filtros'
              : 'Aún no hay compras registradas'}
          </h3>

          {hayFiltrosActivos ? (
            <button
              type="button"
              onClick={limpiarFiltros}
              className="mt-4 min-h-11 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
            >
              Limpiar filtros
            </button>
          ) : (
            !esConsulta && (
              <Link
                to="/compras/nueva"
                className="mt-5 inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700"
              >
                <Plus size={20} />
                Nueva compra
              </Link>
            )
          )}
        </div>
      )
    }

    return (
      <>
        {compras.length >= LIMITE_COMPRAS && (
          <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Se muestran las {LIMITE_COMPRAS} compras más recientes. Usa
            los filtros de fecha para acotar la búsqueda.
          </p>
        )}

        {/* Mobile: tarjetas */}
        <div className="space-y-3 md:hidden">
          {comprasPaginadas.map((compra) => {
            const monto = obtenerMontoCompra(
              compra,
              recepciones.get(compra.id)
            )
            const esPrevia = compra.modalidad === 'PREVIA'

            return (
              <div
                key={compra.id}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900">
                      {compra.codigo_compra}
                    </p>

                    <p className="mt-0.5 text-xs text-slate-500">
                      {formatearFechaPos(compra.fecha_compra)}
                    </p>
                  </div>

                  {renderCasilla(compra)}
                </div>

                <div className="mt-2">{renderBadges(compra)}</div>

                <p className="mt-3 truncate font-semibold text-slate-900">
                  {nombreProveedorCompra(compra)}
                </p>

                <p className="truncate text-sm text-slate-600">
                  {compra.producto_nombre || '—'}
                </p>

                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3 text-sm">
                  <MiniDato
                    etiqueta={esPrevia ? 'Kg acordados' : 'Kg netos'}
                    valor={formatearKilosCompra(
                      esPrevia && !Number(compra.peso_neto_kg)
                        ? compra.cantidad_acordada_kg
                        : compra.peso_neto_kg
                    )}
                  />

                  <MiniDato
                    etiqueta="Precio/kg"
                    valor={formatearMontoCompra(compra.precio_kilo)}
                  />

                  <div className="col-span-2">
                    <MiniDato
                      etiqueta={monto.etiqueta}
                      valor={formatearMontoCompra(monto.valor)}
                      destacado={
                        compra.estado !== 'anulada' && monto.definitivo
                      }
                    />
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  {renderAcciones(compra, true)}
                </div>
              </div>
            )
          })}
        </div>

        {/* Tablet / desktop: tabla */}
        <div className="hidden w-full overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm md:block">
          <table className="w-full text-left text-sm lg:min-w-[1100px]">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                {puedeFacturar && <th className="w-10 px-4 py-3" />}
                <th className="px-4 py-3 lg:px-6">N° compra</th>
                <th className="px-4 py-3 lg:px-6">Fecha</th>
                <th className="px-4 py-3 lg:px-6">Proveedor</th>
                <th className="hidden px-4 py-3 lg:table-cell lg:px-6">
                  Producto
                </th>
                <th className="px-4 py-3 text-right lg:px-6">
                  Kg
                </th>
                <th className="hidden px-4 py-3 text-right lg:table-cell lg:px-6">
                  Precio/kg
                </th>
                <th className="px-4 py-3 text-right lg:px-6">Monto</th>
                <th className="px-4 py-3 lg:px-6">Estado</th>
                <th className="px-4 py-3 text-right lg:px-6">
                  Acciones
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {comprasPaginadas.map((compra) => {
                const monto = obtenerMontoCompra(
                  compra,
                  recepciones.get(compra.id)
                )
                const esPrevia = compra.modalidad === 'PREVIA'

                return (
                  <tr key={compra.id}>
                    {puedeFacturar && (
                      <td className="px-4 py-4">{renderCasilla(compra)}</td>
                    )}

                    <td className="whitespace-nowrap px-4 py-4 font-semibold text-slate-900 lg:px-6">
                      {compra.codigo_compra}
                    </td>

                    <td className="whitespace-nowrap px-4 py-4 text-slate-600 lg:px-6">
                      {formatearFechaPos(compra.fecha_compra)}
                    </td>

                    <td className="px-4 py-4 lg:px-6">
                      <p className="font-semibold text-slate-900">
                        {nombreProveedorCompra(compra)}
                      </p>

                      <p className="text-xs text-slate-500 lg:hidden">
                        {compra.producto_nombre}
                      </p>
                    </td>

                    <td className="hidden px-4 py-4 text-slate-600 lg:table-cell lg:px-6">
                      {compra.producto_nombre || '—'}
                    </td>

                    <td className="whitespace-nowrap px-4 py-4 text-right tabular-nums text-slate-700 lg:px-6">
                      {formatearKilosCompra(
                        esPrevia && !Number(compra.peso_neto_kg)
                          ? compra.cantidad_acordada_kg
                          : compra.peso_neto_kg
                      )}
                    </td>

                    <td className="hidden whitespace-nowrap px-4 py-4 text-right tabular-nums text-slate-600 lg:table-cell lg:px-6">
                      {formatearMontoCompra(compra.precio_kilo)}
                    </td>

                    <td
                      className={`whitespace-nowrap px-4 py-4 text-right tabular-nums lg:px-6 ${
                        compra.estado === 'anulada'
                          ? 'text-slate-400 line-through'
                          : monto.definitivo
                            ? 'font-semibold text-slate-900'
                            : 'text-slate-500'
                      }`}
                    >
                      {formatearMontoCompra(monto.valor)}

                      <p className="text-xs font-normal text-slate-400 no-underline">
                        {monto.etiqueta}
                      </p>
                    </td>

                    <td className="px-4 py-4 lg:px-6">
                      {renderBadges(compra)}
                    </td>

                    <td className="px-4 py-4 lg:px-6">
                      <div className="flex justify-end gap-2">
                        {renderAcciones(compra, false)}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <Paginacion
          pagina={pagina}
          totalPaginas={totalPaginas}
          tamanioPagina={tamanioPagina}
          totalRegistros={comprasFiltradas.length}
          indiceInicio={indiceInicio}
          indiceFin={indiceFin}
          etiquetaRegistros="compras"
          onCambiarPagina={setPagina}
          onCambiarTamanio={setTamanioPagina}
        />
      </>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-6 flex flex-col justify-between gap-4 sm:mb-8 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            Abastecimiento
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Historial de abastecimiento
          </h2>

          <p className="mt-2 text-slate-500">
            Compras, recepciones y facturas de{' '}
            {empresaActual?.nombre_fantasia}.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={cargarCompras}
            disabled={cargando}
            className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none"
          >
            <RefreshCw
              size={18}
              className={cargando ? 'animate-spin' : ''}
            />
            Actualizar
          </button>

          {puedeFacturar && (
            <button
              type="button"
              onClick={abrirFactura}
              disabled={seleccion.length === 0}
              className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-lg border border-emerald-600 px-5 py-3 font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
            >
              <FileText size={18} />
              Registrar factura
              {seleccion.length > 0 && ` (${seleccion.length})`}
            </button>
          )}

          {!esConsulta && (
            <Link
              to="/compras/nueva"
              className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700 sm:flex-none"
            >
              <Plus size={20} />
              Nueva compra
            </Link>
          )}
        </div>
      </div>

      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <CampoFiltro
            label="Buscar proveedor"
            className="sm:col-span-2 lg:col-span-1"
          >
            <div className="relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="text"
                value={busqueda}
                onChange={(event) => setBusqueda(event.target.value)}
                placeholder="Nombre del proveedor..."
                className={estiloInputBusqueda}
              />
            </div>
          </CampoFiltro>

          <CampoFiltro label="Fecha desde">
            <input
              type="date"
              value={filtroFechaDesde}
              onChange={(event) =>
                setFiltroFechaDesde(event.target.value)
              }
              className={estiloInputFiltro}
            />
          </CampoFiltro>

          <CampoFiltro label="Fecha hasta">
            <input
              type="date"
              value={filtroFechaHasta}
              onChange={(event) =>
                setFiltroFechaHasta(event.target.value)
              }
              className={estiloInputFiltro}
            />
          </CampoFiltro>

          <CampoFiltro label="Producto">
            <select
              value={filtroProducto}
              onChange={(event) =>
                setFiltroProducto(event.target.value)
              }
              className={estiloInputFiltro}
            >
              <option value="">Todos</option>
              {productos.map((producto) => (
                <option
                  key={producto.id}
                  value={producto.id}
                >
                  {producto.nombre}
                </option>
              ))}
            </select>
          </CampoFiltro>

          <CampoFiltro label="Estado">
            <select
              value={filtroEstado}
              onChange={(event) =>
                setFiltroEstado(event.target.value)
              }
              className={estiloInputFiltro}
            >
              <option value="">Todos</option>
              <option value="pendiente_recepcion">Pendientes de recepción</option>
              <option value="registrada">Registradas</option>
              <option value="anulada">Anuladas</option>
            </select>
          </CampoFiltro>
        </div>

        {hayFiltrosActivos && (
          <div className="mt-4 flex justify-end border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={limpiarFiltros}
              className="min-h-11 text-sm font-semibold text-slate-500 hover:text-red-600"
            >
              Limpiar filtros
            </button>
          </div>
        )}
      </div>

      {renderContenido()}

      <RecepcionModal
        abierto={Boolean(compraRecepcion)}
        compra={compraRecepcion}
        recepcion={
          compraRecepcion ? recepciones.get(compraRecepcion.id) : null
        }
        empresaId={empresaId}
        puedeValidar={puedeValidarRecepcion(rol)}
        onCerrar={() => setCompraRecepcion(null)}
        onGuardada={alGuardarRecepcion}
      />

      <FacturaModal
        abierto={facturaAbierta}
        compras={comprasSeleccionadas}
        empresaId={empresaId}
        onCerrar={() => setFacturaAbierta(false)}
        onRegistrada={alRegistrarFactura}
        onVerGasto={verGastoExistente}
      />

      <EliminarCompraModal
        compra={compraEliminar}
        empresaId={empresaId}
        onCancelar={() => setCompraEliminar(null)}
        onEliminada={alEliminarCompra}
      />

      <ModalMensaje {...modal} />
    </div>
  )
}

export default Compras
